import { TypeFormation } from './group.types';
import { AttendanceStatus } from './grade.types';

// ==================== GRADE ABSENCE STATUS ====================

export enum GradeAbsenceStatus {
  NONE = 'NONE',
  ABSENT_EXCUSED = 'ABSENT_EXCUSED',
  ABSENT_UNEXCUSED = 'ABSENT_UNEXCUSED',
}

export enum StudentGradeStatusEnum {
  INCOMPLETE = 'INCOMPLETE',
  VALIDATED = 'VALIDATED',
  FAILED = 'FAILED',
  RATTRAPAGE = 'RATTRAPAGE',
}

// ==================== DASHBOARD TYPES ====================

export interface TodayScheduleItem {
  scheduleEntryId: string;
  groupeId: string;
  groupeName: string;
  moduleElementId: string;
  moduleElementName: string;
  filiereName: string;
  startTime: string;
  endTime: string;
  roomName: string | null;
  /** Other groups attending the same shared session (optional: older backends omit it) */
  sharedWithGroups?: string[];
  status: 'completed' | 'in-progress' | 'upcoming';
  attendanceMarked: boolean;
}

export interface PendingTask {
  type: 'grade_entry' | 'attendance' | 'assignment';
  title: string;
  description: string;
  groupeId: string;
  moduleElementId: string;
  priority: 'high' | 'medium' | 'low';
}

export interface QuickStats {
  gradeCompletionRate: number;
  attendanceCompletionRate: number;
  materialsUploaded: number;
  assignmentsCreated: number;
}

export interface ProfessorDashboardStats {
  totalClasses: number;
  totalStudents: number;
  totalHoursThisMonth: number;
  pendingGradeEntries: number;
  pendingAttendance: number;
  todaySchedule: TodayScheduleItem[];
  pendingTasks: PendingTask[];
  quickStats: QuickStats;
}

// ==================== MY CLASSES TYPES ====================

export interface ScheduleInfo {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  roomName: string | null;
}

export interface GradeStatus {
  configured: boolean;
  componentCount: number;
  totalStudents: number;
  gradedStudents: number;
  percentageComplete: number;
  isFinalized: boolean;
}

export interface AttendanceStats {
  totalSessions: number;
  completedSessions: number;
  percentageComplete: number;
}

export interface ContentStats {
  syllabusUploaded: boolean;
  materialsCount: number;
  announcementsCount: number;
  assignmentsCount: number;
}

export interface ProfessorClassInfo {
  groupeId: string;
  groupeName: string;
  /** Other groups taught together with this class in a shared session (optional) */
  sharedWithGroups?: string[];
  moduleElementId: string;
  moduleElementName: string;
  moduleCode: string;
  moduleName: string;
  filiereId: string;
  filiereCode: string;
  filiereName: string;
  niveau: number;
  // Absolute semester of the module (S1…S6) from the filière structure.
  semester: number | null;
  semesterName: string | null;
  typeFormation: TypeFormation;
  campusId: string | null;
  campusName: string | null;
  studentCount: number;
  scheduleInfo: ScheduleInfo[];
  gradeStatus: GradeStatus;
  attendanceStats: AttendanceStats;
  contentStats: ContentStats;
}

export interface ClassStudentInfo {
  id: string;
  studentCode: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  photoUrl: string | null;
  attendanceRate: number;
  totalAbsences: number;
  totalPresences: number;
  totalLates: number;
  // Student who left the group but has attendance recorded in this class
  isFormerMember?: boolean;
}

export interface ClassDetailInfo extends ProfessorClassInfo {
  academicYearId: string;
  academicYearName: string;
  students: ClassStudentInfo[];
}

// ==================== GRADE CONFIGURATION TYPES ====================

export interface GradeComponent {
  id?: string;
  code: string;
  name: string;
  percentage: number;
  maxScore: number;
  isRequired?: boolean;
  sortOrder?: number;
  isActive?: boolean;
}

export interface GradeConfigResponse {
  moduleElementId: string;
  groupeId: string;
  academicYearId: string;
  professorId: string;
  components: GradeComponent[];
  totalPercentage: number;
  isValid: boolean;
  isFinalized: boolean;
}

export interface CreateGradeConfigDto {
  components: GradeComponent[];
}

export interface GradeTemplateResponse {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  components: {
    code: string;
    name: string;
    percentage: number;
    maxScore: number;
  }[];
}

// ==================== GRADE ENTRY TYPES ====================

export interface ComponentGrade {
  componentCode: string;
  componentName: string;
  percentage: number;
  maxScore: number;
  score: number | null;
  absenceStatus: GradeAbsenceStatus;
  gradedAt: string | null;
  // Rattrapage traceability
  isFromRattrapage: boolean;
  originalScore: number | null;
  rattrapageId: string | null;
}

export interface StudentGradeResponse {
  studentId: string;
  studentCode: string;
  firstName: string;
  lastName: string;
  photoUrl: string | null;
  componentGrades: ComponentGrade[];
  averageGrade: number | null;
  isComplete: boolean;
  status: StudentGradeStatusEnum;
  statusReason?: string;
  // Student who left the group but already holds a grade here (still editable)
  isFormerMember?: boolean;
}

export interface StudentGradeEntry {
  studentId: string;
  componentCode: string;
  score?: number | null;
  absenceStatus?: GradeAbsenceStatus;
  comments?: string;
}

export interface SaveGradesDto {
  grades: StudentGradeEntry[];
}

// ==================== ATTENDANCE TYPES ====================

export interface AttendanceRecord {
  id: string;
  studentId: string;
  studentCode: string;
  studentName: string;
  sessionDate: string;
  status: AttendanceStatus | 'NOT_MARKED';
  lateMinutes: number | null;
  justification: string | null;
  recordedBy: string;
  recordedAt: string;
  // Student who left the group but was marked at this session (read-only)
  isFormerMember?: boolean;
}

export interface AttendanceSessionResponse {
  sessionDate: string;
  scheduleSessionId: string | null;
  totalStudents: number;
  presentCount: number;
  absentExcusedCount: number;
  absentUnexcusedCount: number;
  lateCount: number;
  notMarkedCount: number;
  isComplete: boolean;
  records: AttendanceRecord[];
}

export interface AttendanceRecordInput {
  studentId: string;
  status: AttendanceStatus;
  lateMinutes?: number;
  justification?: string;
}

export interface MarkAttendanceDto {
  sessionDate: string;
  scheduleSessionId: string;
  attendanceRecords: AttendanceRecordInput[];
}

export interface UpdateAttendanceDto {
  status: AttendanceStatus;
  lateMinutes?: number;
  justification?: string;
}

export interface StudentAttendanceStats {
  studentId: string;
  studentCode: string;
  studentName: string;
  totalPresences: number;
  totalAbsencesExcused: number;
  totalAbsencesUnexcused: number;
  totalLates: number;
  attendanceRate: number;
  exceedsAbsenceThreshold: boolean;
  // Student who left the group but has marks recorded in this class
  isFormerMember?: boolean;
}

// Rates over every mark recorded for the group, not an average of per-student
// rates (which would over-weight students with few marked sessions).
export interface GroupAttendanceRates {
  totalMarks: number;
  presenceRate: number; // présents + retards
  lateRate: number;
  absenceExcusedRate: number;
  absenceUnexcusedRate: number;
  totalPresences: number;
  totalLates: number;
  totalAbsencesExcused: number;
  totalAbsencesUnexcused: number;
  studentsAboveThreshold: number;
}

export interface AttendanceStatsResponse {
  totalSessions: number;
  completedSessions: number;
  totalStudents: number;
  averageAttendanceRate: number;
  absenceThreshold: number;
  lateThresholdMinutes: number;
  groupRates: GroupAttendanceRates;
  studentStats: StudentAttendanceStats[];
}

// ==================== FILTER & QUERY TYPES ====================

export interface GetMyClassesQueryDto {
  academicYearId?: string;
  semester?: number;
}

export interface GetAttendanceQueryDto {
  startDate?: string;
  endDate?: string;
  studentId?: string;
}

// ==================== API RESPONSE TYPES ====================

export interface SaveGradesResponse {
  savedCount: number;
}

export interface FinalizeGradesResponse {
  success: boolean;
  message: string;
  rattrapagesCreated?: number;
  // Components left blank and recorded as 0 by the finalization.
  zerosWritten?: number;
}

export interface MarkAttendanceResponse {
  success: boolean;
  markedCount: number;
}

export interface UpdateAttendanceResponse {
  success: boolean;
}

// ==================== SYLLABUS TYPES ====================

export interface SyllabusResponse {
  id: string;
  moduleElementId: string;
  professorId: string;
  /** Group this syllabus belongs to (null only for unreachable legacy rows). */
  groupeId: string | null;
  academicYearId: string;
  fileName: string | null;
  fileUrl: string | null;
  fileType: string | null;
  fileSize: number | null;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UploadSyllabusDto {
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
}

export interface UpdateSyllabusDto {
  fileName?: string;
  fileUrl?: string;
  fileType?: string;
  fileSize?: number;
  isPublished?: boolean;
}

// ==================== SCHEDULED SESSIONS TYPES ====================

export interface ScheduledSessionInfo {
  id: string;
  scheduleEntryId: string;
  date: string;
  startTime: string;
  endTime: string;
  roomName: string | null;
  isCancelled: boolean;
  isToday: boolean;
  isPast: boolean;
  isFuture: boolean;
  canEdit: boolean;
  attendanceStatus: 'not_filled' | 'partial' | 'complete' | 'cancelled';
  presentCount: number;
  absentExcusedCount: number;
  absentUnexcusedCount: number;
  lateCount: number;
  totalStudents: number;
}

export interface ScheduledSessionsResponse {
  todaySessions: ScheduledSessionInfo[];
  pastSessions: ScheduledSessionInfo[];
  futureSessions: ScheduledSessionInfo[];
  totalStudents: number;
  isSemesterActive: boolean;
}

// ==================== COURSE CONTENT TYPES ====================

export type CourseMaterialType = 'DOCUMENT' | 'VIDEO' | 'LINK' | 'PRESENTATION' | 'EXERCISE' | 'OTHER';

export interface MaterialResponse {
  id: string;
  chapterId: string | null;
  title: string;
  description: string | null;
  fileName: string | null;
  fileUrl: string | null;
  fileType: string | null;
  fileSize: number | null;
  externalUrl: string | null;
  materialType: CourseMaterialType;
  sortOrder: number;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChapterResponse {
  id: string;
  title: string;
  description: string | null;
  sortOrder: number;
  isPublished: boolean;
  isSection: boolean; // true = custom section (purple), false = regular chapter (blue)
  createdAt: string;
  updatedAt: string;
  materialsCount: number;
  materials: MaterialResponse[];
}

export interface CourseContentResponse {
  chapters: ChapterResponse[];
  generalMaterials: MaterialResponse[];
  generalSectionTitle: string;
}

export interface CreateChapterDto {
  title: string;
  description?: string;
  isPublished?: boolean;
  isSection?: boolean; // true = custom section, false = regular chapter
}

export interface UpdateChapterDto {
  title?: string;
  description?: string;
  isPublished?: boolean;
  isSection?: boolean;
}

export interface CreateMaterialDto {
  chapterId?: string;
  title: string;
  description?: string;
  fileName?: string;
  fileUrl?: string;
  fileType?: string;
  fileSize?: number;
  externalUrl?: string;
  materialType?: CourseMaterialType;
  isPublished?: boolean;
}

export interface UpdateMaterialDto {
  chapterId?: string | null;
  title?: string;
  description?: string;
  fileName?: string;
  fileUrl?: string;
  fileType?: string;
  fileSize?: number;
  externalUrl?: string;
  materialType?: CourseMaterialType;
  isPublished?: boolean;
}

// ==================== ANNOUNCEMENT TYPES ====================

export interface AnnouncementResponse {
  id: string;
  title: string;
  content: string;
  isPinned: boolean;
  isPublished: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAnnouncementDto {
  title: string;
  content: string;
  isPinned?: boolean;
}

export interface UpdateAnnouncementDto {
  title?: string;
  content?: string;
  isPinned?: boolean;
}

// ==================== ASSIGNMENT TYPES ====================

export interface AttachmentFile {
  url: string;
  fileName: string;
  fileType?: string;
  fileSize?: number;
}

export interface AssignmentResponse {
  id: string;
  title: string;
  description: string | null;
  instructions: string | null;
  dueDate: string;
  maxScore: number;
  weight: number;
  isActive: boolean;
  allowLateSubmission: boolean;
  latePenaltyPercent: number | null;
  lateSubmissionDays: number | null;
  acceptedFileTypes: string[] | null;
  attachmentUrl: string | null;
  attachmentFileName: string | null;
  attachments: AttachmentFile[] | null;
  includeInFinalGrade: boolean;
  gradeComponentCode: string | null;
  gradeWeight: number | null;
  rubricUrl: string | null;
  rubricFileName: string | null;
  rubricContent: string | null;
  submissionsCount: number;
  gradedCount: number;
  isPastDue: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SubmissionResponse {
  id: string;
  studentId: string;
  studentCode: string;
  studentName: string;
  studentEmail: string | null;
  fileUrl: string | null;
  fileName: string | null;
  fileSize: number | null;
  content: string | null;
  submittedAt: string;
  isLate: boolean;
  lateDays: number | null;
  score: number | null;
  feedback: string | null;
  gradedAt: string | null;
  status: 'pending' | 'graded' | 'late' | 'missing';
}

export interface AssignmentDetailResponse extends AssignmentResponse {
  totalStudents: number;
  submissions: SubmissionResponse[];
}

export interface CreateAssignmentDto {
  title: string;
  description?: string;
  instructions?: string;
  dueDate: string;
  maxScore?: number;
  allowLateSubmission?: boolean;
  latePenaltyPercent?: number;
  lateSubmissionDays?: number;
  acceptedFileTypes?: string[];
  attachmentUrl?: string;
  attachmentFileName?: string;
  attachments?: AttachmentFile[];
  includeInFinalGrade?: boolean;
  gradeComponentCode?: string;
  gradeWeight?: number;
  rubricUrl?: string;
  rubricFileName?: string;
  rubricContent?: string;
}

export interface UpdateAssignmentDto {
  title?: string;
  description?: string;
  instructions?: string;
  dueDate?: string;
  maxScore?: number;
  allowLateSubmission?: boolean;
  latePenaltyPercent?: number;
  lateSubmissionDays?: number;
  acceptedFileTypes?: string[];
  attachmentUrl?: string | null;
  attachmentFileName?: string | null;
  attachments?: AttachmentFile[];
  includeInFinalGrade?: boolean;
  gradeComponentCode?: string | null;
  gradeWeight?: number;
  rubricUrl?: string | null;
  rubricFileName?: string | null;
  rubricContent?: string;
  isActive?: boolean;
}

export interface GradeSubmissionDto {
  score?: number | null;
  feedback?: string;
}

// ==================== DAY OF WEEK HELPERS ====================

export const DAY_OF_WEEK_NAMES: Record<number, string> = {
  0: 'Dimanche',
  1: 'Lundi',
  2: 'Mardi',
  3: 'Mercredi',
  4: 'Jeudi',
  5: 'Vendredi',
  6: 'Samedi',
};

export const getDayName = (dayOfWeek: number): string => {
  return DAY_OF_WEEK_NAMES[dayOfWeek] || '';
};

// One dated exam or rattrapage session on the professor's radar
// (GET /professor/exams). Mirrors mobile/src/api/professor/types.ts.
export interface ProfessorExamInfo {
  id: string;
  scheduleEntryId: string;
  moduleElementId: string;
  moduleElementName: string;
  groupeId: string;
  groupeName: string;
  filiereName: string;
  filiereCode: string;
  campusName: string | null;
  roomName: string | null;
  date: string;
  startTime: string;
  endTime: string;
  isRattrapage: boolean;
  // false = proctoring only: not a course the professor teaches
  isOwnClass: boolean;
  isProctor: boolean;
}
